import { addInkindToGroup } from '@gik/api/inkinds/groups';
import { useUserOwnedOrOrganizingPages } from '@gik/api/inkinds/userPages';
import type { Group } from '@gik/core/models/gik/Group';
import type { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import { useUserStore } from '@gik/core/store/UserStore';
import type { UIComponent } from '@gik/core/types/UI';
import { LayoutTypes } from '@gik/core/types/layouts';
import { renderPortal } from '@gik/core/utils/RenderPortal';
import { useBemCN } from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { InkindPageCardBase } from '@gik/inkind-page/components/InkindPageCard/InkindPageCardBase';
import { Button } from '@gik/ui-legacy/Button';
import { Checkbox } from '@gik/ui-legacy/Checkbox';
import { LoadingSpinner } from '@gik/ui-legacy/LoadingSpinner';
import { SearchInput } from '@gik/ui-legacy/SearchInput';
import { Tooltip } from '@gik/ui-legacy/Tooltip/Tooltip';
import { UI } from '@gik/ui-legacy/UIManager';
import he from 'he';
import debounce from 'lodash/debounce';
import React from 'react';

export type AddInkindPagesModalProps = {
  group: Group;
  /** Portal target for the modal footer buttons (provided by `UI.dialog`). */
  buttonsPortal: React.MutableRefObject<Element>;
  /** Close the containing dialog, passing the Pages that were successfully added (empty if none). */
  onClose?: (addedPages?: InkindPageAPIModel[]) => void;
} & UIComponent;

const blockName = 'add-inkind-pages-modal';

/**
 * A single selectable Page row. Pages already in the group are rendered
 * checked and disabled — the user can't remove them from here.
 */
function AddInkindPagesModalRow({
  page,
  checked,
  disabled,
  reason,
  onToggle,
}: {
  page: InkindPageAPIModel;
  checked: boolean;
  disabled: boolean;
  /** When set, the Page can't be added; shown as a hover tooltip (e.g. already in another group). */
  reason?: string;
  onToggle: () => void;
}): React.ReactElement {
  const bem = useBemCN(blockName);

  // The dotnet Page model returns `, undefined` when a location is missing.
  const location = page.location === ', undefined' ? undefined : page.location;

  const checkbox = (
    <Checkbox
      {...bem('checkbox')}
      checked={checked}
      disabled={disabled}
      onValueChange={onToggle}
      tabIndex={disabled ? -1 : 0}
    />
  );

  return (
    <label {...bem('row', [{ disabled }, { selected: checked }])}>
      <InkindPageCardBase
        {...bem('card')}
        layout={LayoutTypes.horizontal}
        imageUrl={page.thumb_medium || page.imgSrc}
        title={he.decode(page.title || '')}
        location={location}
      />
      <div {...bem('control')}>
        {/* Scope the tooltip to the checkbox: Checkbox forwards its ref to the
            hidden <input>, so a span wrapper gives Tippy a stable visible anchor. */}
        {reason ? (
          <Tooltip text={reason} allowOnMobile>
            <span {...bem('checkbox-wrap')}>{checkbox}</span>
          </Tooltip>
        ) : (
          checkbox
        )}
      </div>
    </label>
  );
}

function AddInkindPagesModalContentComp({
  group,
  buttonsPortal,
  onClose = noop,
  className,
  ...otherProps
}: AddInkindPagesModalProps): React.ReactElement {
  const bem = useBemCN(blockName);

  const userId = useUserStore(state => state.id);

  // Only Pages the current user can *manage* are addable to a Group (adding
  // requires manage permission — read-only organizers, e.g. wallet/gift
  // recipients, can't). Pages already in a *different* Group are still returned
  // (a Page belongs to at most one Group) so we can show them grayed-out with a
  // reason, rather than hiding them and confusing the user.
  const {
    data: pages,
    isValidating,
    mutate,
  } = useUserOwnedOrOrganizingPages(userId, {
    allPages: true,
    editableOnly: true,
  });

  const [search, setSearch] = React.useState<string>('');
  // routeIds of Pages the user has newly selected this session.
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set());
  const [submitting, setSubmitting] = React.useState(false);

  const isAlreadyInGroup = React.useCallback((page: InkindPageAPIModel) => page.groupId === group?.id, [group?.id]);

  // A Page in a *different* Group can't be added here (belongs to at most one
  // Group). Shown grayed-out with this reason instead of being hidden.
  const isInOtherGroup = React.useCallback(
    (page: InkindPageAPIModel) => Boolean(page.groupId) && page.groupId !== group?.id,
    [group?.id]
  );

  const toggle = React.useCallback((routeId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(routeId)) {
        next.delete(routeId);
      } else {
        next.add(routeId);
      }
      return next;
    });
  }, []);

  const filteredPages = React.useMemo(() => {
    if (!pages) return [];

    const term = search.trim().toLowerCase();
    if (!term) return pages;

    return pages.filter(page => {
      const title = he.decode(page.title || '').toLowerCase();
      const location = (page.location || '').toLowerCase();
      return title.includes(term) || location.includes(term);
    });
  }, [pages, search]);

  const handleAdd = React.useCallback(async () => {
    const routeIds = Array.from(selected);
    if (routeIds.length === 0) {
      onClose([]);
      return;
    }

    setSubmitting(true);

    const results = await Promise.all(
      routeIds.map(async routeId => {
        try {
          const response = await addInkindToGroup(group.id, routeId);
          return { routeId, ok: response.ok };
        } catch {
          return { routeId, ok: false };
        }
      })
    );

    setSubmitting(false);

    const addedRouteIds = new Set(results.filter(result => result.ok).map(result => result.routeId));
    const failedCount = results.length - addedRouteIds.size;
    const addedPages = (pages ?? []).filter(page => addedRouteIds.has(page.routeId));

    if (addedPages.length > 0) {
      UI.notifySuccess(
        addedPages.length === 1
          ? 'InKind Page successfully added to the group'
          : `${addedPages.length} InKind Pages successfully added to the group`
      );
      // Refresh the organizing/owned Pages so re-opening reflects new membership.
      await mutate();
    }

    if (failedCount > 0) {
      UI.notifyError(
        failedCount === 1
          ? 'One InKind Page could not be added to the group'
          : `${failedCount} InKind Pages could not be added to the group`
      );
    }

    onClose(addedPages);
  }, [group?.id, mutate, onClose, pages, selected]);

  const isLoading = !pages && isValidating;
  const hasNoPages = !isLoading && filteredPages.length === 0;

  const footerButtons = (
    <>
      <Button variant="default-light" onClick={() => onClose([])} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleAdd} loading={submitting} disabled={selected.size === 0}>
        Add to Group
      </Button>
    </>
  );

  return (
    <div {...bem(undefined, undefined, className)} {...otherProps}>
      <p {...bem('intro')}>
        Browse Pages you organize and add them to <strong>{group ? he.decode(group.title || '') : ''}</strong>.
      </p>

      <SearchInput
        {...bem('search')}
        clearable
        value={search}
        placeholder="Search your InKind Pages..."
        onValueChange={debounce(setSearch, 200)}
      />

      <div {...bem('list')}>
        {isLoading && <LoadingSpinner center />}

        {hasNoPages && (
          <div {...bem('empty')}>
            {search ? `No results for "${search}"` : 'You don’t organize any InKind Pages yet.'}
          </div>
        )}

        {!isLoading &&
          filteredPages.map(page => {
            const alreadyInGroup = isAlreadyInGroup(page);
            const inOtherGroup = isInOtherGroup(page);
            return (
              <AddInkindPagesModalRow
                key={page.routeId}
                page={page}
                disabled={alreadyInGroup || inOtherGroup}
                reason={inOtherGroup ? 'Page already in another Group' : undefined}
                checked={alreadyInGroup || selected.has(page.routeId)}
                onToggle={() => toggle(page.routeId)}
              />
            );
          })}
      </div>

      {renderPortal(footerButtons, () => buttonsPortal.current)}
    </div>
  );
}

export const AddInkindPagesModalContent = withComponentErrorBoundary(AddInkindPagesModalContentComp);
